import { useQuery } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { ShiftStatusResponse } from '@/api/models';
import type { ErrorResponse } from '@/api/handlers/responses';
import { getUser } from '@/utils/authSession';
import { ShiftQueryKeys } from './shiftQueryKeys';
import { SHIFT_REFRESH_INTERVAL_MS } from './useCurrentShiftQuery';

/**
 * The status of a shift this device remembers (`GET /shifts/{id}/status`),
 * behind the sale point handover guard (SEL-30). Issues no request until there
 * is a shift to ask about. The shift can be closed on another sale point or by
 * an admin with nothing telling this one, so it re-reads on window focus and on
 * the shift refresh interval. That re-read is also its retry: a failed read
 * surfaces at once rather than holding the guard through backoff. Unwraps the
 * envelope and throws its `ErrorResponse`, so `SHIFT_NOT_FOUND` reaches the
 * caller as `error.code`. The key carries the signed-in seller.
 */
export const useShiftStatusQuery = (shiftId: string | null) => {
    const { apiClient } = useApi();

    return useQuery<ShiftStatusResponse, ErrorResponse>({
        queryKey: ShiftQueryKeys.status(shiftId ?? '', getUser()?.id ?? null),
        queryFn: async () => {
            const result = await apiClient.shifts.getStatus(shiftId ?? '');
            if (!result.success) {
                throw result.error;
            }
            return result.response;
        },
        enabled: shiftId !== null,
        staleTime: 0,
        retry: false,
        refetchOnWindowFocus: true,
        refetchInterval: SHIFT_REFRESH_INTERVAL_MS,
    });
};
